import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subscription, debounceTime, distinctUntilChanged, finalize, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { DisbursementCertificationService } from 'src/app/services/api/disbursement/disbursement.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import {
  DisbursementApprovalTrackEntry,
  DisbursementCertificationModel,
  DisbursementCertificationListParams,
  DisbursementDocType,
  DisbursementStatusCode,
  DISBURSEMENT_CALC_DOC_TYPES,
  DISBURSEMENT_DOC_LABELS,
  DISBURSEMENT_SCHEDULE_DOC_TYPES,
  DISBURSEMENT_STATUS_LABELS,
} from 'src/app/services/models/disbursement/disbursement.model';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { SharedModule } from 'src/shared/shared.module';
import {
  buildAcademicTermNameOptions,
  buildAcademicYearOptions,
  defaultAcademicTerm,
  findAcademicTerm,
  toBuddhistYear,
} from 'src/utils/academic-term-options';
import { RoleID } from 'src/utils/enum/role.enum';
import {
  DisbursementRevisionDialogComponent,
  DisbursementRevisionDialogResult,
} from '../_shared/disbursement-revision-dialog/disbursement-revision-dialog.component';

type CertificationTab = 'all' | 'pending';

// The single row-level action a certifier can take to move the certification
// forward. Resolved purely from (active role, row.statusCode) — never from
// anything client-supplied on the row itself.
type PrimaryActionKind =
  | 'teacher_certify'
  | 'head_certify'
  | 'finance_approve'
  | 'vice_dean_certify'
  | 'revision_approve';

// The "send it back" action. v1.1: teacher/head/finance/vice_dean_admin all
// send back through the same reason-only request_revision endpoint (all
// routed to รองวิชาการ) — vice_dean_academic's is the reviewer's reject,
// dispatched to a different endpoint (revision_reject).
type RevisionActionKind =
  | 'teacher_revision'
  | 'head_revision'
  | 'finance_revision'
  | 'vice_dean_admin_revision'
  | 'vice_dean_academic_reject';

interface CertificationRow {
  id: number;
  teacherName: string;
  teacherEmail: string;
  departmentName: string;
  academicTermLabel: string;
  pathLabel: string;
  statusCode: DisbursementStatusCode;
  statusLabel: string;
  updatedAtLabel: string;
  raw: DisbursementCertificationModel;
}

// v1.2 §4 — one on-the-fly inline preview panel per doc row (overload-memo
// blob -> createObjectURL -> DomSanitizer pattern). Keyed by doc_type; never
// persisted, so closing the detail modal revokes every open object URL.
interface PreviewPanelState {
  loading: boolean;
  error: boolean;
  url: SafeResourceUrl | null;
}

interface CertificationDataSource {
  data: CertificationRow[];
  limit: number;
  offset: number;
  page: number;
  total: number;
  total_page: number;
}

const STATUS_BADGE_CLASS: Record<DisbursementStatusCode, string> = {
  pending_teacher: 'disb-cert-status-pending',
  pending_head_of_dept: 'disb-cert-status-pending',
  pending_finance: 'disb-cert-status-pending',
  pending_vice_dean_admin: 'disb-cert-status-pending',
  pending_bundle: 'disb-cert-status-pending',
  delivered: 'disb-cert-status-success',
  revision_schedule_requested: 'disb-cert-status-revision',
};

@Component({
  selector: 'app-disbursement-certification',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    SharedModule,
    DisbursementRevisionDialogComponent,
  ],
  templateUrl: './disbursement-certification.component.html',
  styleUrl: './disbursement-certification.component.scss',
})
export class DisbursementCertificationComponent implements OnInit, OnDestroy {
  @ViewChild('statusTemplate', { static: true })
  statusTemplate!: TemplateRef<unknown>;
  @ViewChild('pendingActionTemplate', { static: true })
  pendingActionTemplate!: TemplateRef<unknown>;
  @ViewChild('actionTemplate', { static: true })
  actionTemplate!: TemplateRef<unknown>;
  @ViewChild('primaryConfirmDialog')
  primaryConfirmDialog!: ConfirmDialogComponent;
  @ViewChild('revisionDialog')
  revisionDialog!: DisbursementRevisionDialogComponent;

  filterForm = inject(FormBuilder).group({
    academicTermId: [null as number | null],
    academicTerm: [null as string | null],
    academicYear: [null as string | null],
    keywords: [''],
  });

  columns: unknown[] = [];
  datasource = signal<CertificationDataSource>(this.createDataSource([]));
  tableLoading = signal(false);
  actionLoading = signal(false);
  selectedRow = signal<CertificationRow | null>(null);
  detailOpen = signal(false);
  activeTab = signal<CertificationTab>('pending');
  academicTermNameOptions: string[] = [];
  academicYearOptions: string[] = [];

  primaryActionKind = signal<PrimaryActionKind | null>(null);
  revisionActionKind = signal<RevisionActionKind | null>(null);

  // v1.2 §4 — one inline preview panel per doc_type, keyed on the string
  // (doc_2/doc_3/doc_4/doc_5/doc_7). Reset whenever the detail modal opens on
  // a different row/closes (see onView/onCloseDetail).
  previewPanels = signal<Partial<Record<string, PreviewPanelState>>>({});

  pageSizeOptions = [10, 20, 50];

  private currentPage = 1;
  private currentLimit = 10;
  private currentSortParams: DatatableSortParams = { sort: null, direction: null };
  private academicTerms: AcademicTermYearModel[] = [];
  private readonly subscriptions = new Subscription();
  private readonly sortFieldMap: DatatableSortFieldMap = {
    status: 'status',
    updatedAtLabel: 'updated_at',
  };
  private readonly authService = inject(AuthService);
  private readonly disbursementService = inject(DisbursementCertificationService);
  private readonly studyPeriodService = inject(StudyPeriodService);
  private readonly toast = inject(ToastService);
  private readonly route = inject(ActivatedRoute);
  private readonly sanitizer = inject(DomSanitizer);
  // Raw (unsanitized) blob URLs, kept only so they can be revoked — never
  // rendered directly (the sanitized SafeResourceUrl in previewPanels is what
  // the template binds to).
  private readonly rawPreviewUrls = new Map<string, string>();

  ngOnInit(): void {
    this.initColumns();
    this.listenFilters();
    this.loadFilterOptions();
    this.maybeAutoOpenFocusedCertification();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.revokeAllPreviews();
  }

  get isRevisionReviewRole(): boolean {
    return this.authService.getActiveRoleId() === RoleID.VICE_DEAN_ACADEMIC;
  }

  onTabChange(tab: CertificationTab): void {
    if (this.activeTab() === tab) {
      return;
    }

    this.activeTab.set(tab);
    this.loadCertifications(1);
  }

  onSearch(): void {
    this.loadCertifications(1);
  }

  onView(row: CertificationRow): void {
    this.revokeAllPreviews();
    this.selectedRow.set(row);
    this.detailOpen.set(true);
  }

  onCloseDetail(): void {
    if (this.actionLoading()) {
      return;
    }

    this.revokeAllPreviews();
    this.detailOpen.set(false);
    this.selectedRow.set(null);
  }

  // Bypasses the actionLoading guard above: called from within a success
  // callback that is still inside actionLoading=true (finalize() only flips
  // it back to false once the source completes/errors, which for a
  // synchronous observable happens after this callback returns) — mirrors
  // teach-compensation-review.component.ts#onConfirmApprove.
  private closeDetailAfterAction(): void {
    this.revokeAllPreviews();
    this.detailOpen.set(false);
    this.selectedRow.set(null);
  }

  // --- v1.2 §4 two-section documents + inline preview ---

  // "เอกสารส่งเบิกที่คำนวณแล้ว" shows only the cert's own path's docs
  // (decision 8) — doc_2+doc_5 for a regular cert, doc_3+doc_4 for an
  // international one.
  calcSectionDocTypes(row: CertificationRow | null): DisbursementDocType[] {
    if (row == null) {
      return [];
    }

    return DISBURSEMENT_CALC_DOC_TYPES[row.raw.path];
  }

  // "ตารางเรียนตารางสอน" — doc_7, same for every path.
  scheduleSectionDocTypes(): DisbursementDocType[] {
    return DISBURSEMENT_SCHEDULE_DOC_TYPES;
  }

  docLabel(docType: string): string {
    return DISBURSEMENT_DOC_LABELS[docType] ?? docType;
  }

  isPreviewExpanded(docType: string): boolean {
    return this.previewPanels()[docType] != null;
  }

  isPreviewLoading(docType: string): boolean {
    return this.previewPanels()[docType]?.loading ?? false;
  }

  previewHasError(docType: string): boolean {
    return this.previewPanels()[docType]?.error ?? false;
  }

  previewUrl(docType: string): SafeResourceUrl | null {
    return this.previewPanels()[docType]?.url ?? null;
  }

  onTogglePreview(docType: DisbursementDocType): void {
    if (this.isPreviewExpanded(docType)) {
      this.collapsePreview(docType);
      return;
    }

    this.expandPreview(docType);
  }

  private expandPreview(docType: DisbursementDocType): void {
    const row = this.selectedRow();
    if (row == null) {
      return;
    }

    this.patchPreviewPanels(docType, { loading: true, error: false, url: null });
    this.disbursementService.previewDocument(row.id, docType).pipe(take(1)).subscribe({
      next: (blob) => {
        const rawUrl = window.URL.createObjectURL(blob);
        this.rawPreviewUrls.set(docType, rawUrl);
        this.patchPreviewPanels(docType, {
          loading: false,
          error: false,
          url: this.sanitizer.bypassSecurityTrustResourceUrl(`${rawUrl}#toolbar=0&navpanes=0`),
        });
      },
      error: (err) => {
        console.error(err);
        this.patchPreviewPanels(docType, { loading: false, error: true, url: null });
      },
    });
  }

  private collapsePreview(docType: string): void {
    this.revokePreview(docType);
    this.previewPanels.update((panels) => {
      const next = { ...panels };
      delete next[docType];
      return next;
    });
  }

  private patchPreviewPanels(docType: string, state: PreviewPanelState): void {
    this.previewPanels.update((panels) => ({ ...panels, [docType]: state }));
  }

  private revokePreview(docType: string): void {
    const rawUrl = this.rawPreviewUrls.get(docType);
    if (rawUrl != null) {
      window.URL.revokeObjectURL(rawUrl);
      this.rawPreviewUrls.delete(docType);
    }
  }

  private revokeAllPreviews(): void {
    this.rawPreviewUrls.forEach((rawUrl) => window.URL.revokeObjectURL(rawUrl));
    this.rawPreviewUrls.clear();
    this.previewPanels.set({});
  }

  // --- v1.2 §3/§4 approval stepper + "รอดำเนินการ" column ---

  // The station currently holding the cert — used for both the detail
  // modal's stepper (highlighted) and the table's compact
  // "รอดำเนินการ: ตำแหน่ง ชื่อ" column.
  currentStationEntry(row: CertificationRow | null): DisbursementApprovalTrackEntry | null {
    return row?.raw.approval_track.find((entry) => entry.state === 'current') ?? null;
  }

  pendingActionLabel(row: CertificationRow | null): string {
    const current = this.currentStationEntry(row);
    if (current == null) {
      return '-';
    }

    const names = current.person_names.length > 0 ? current.person_names.join(', ') : 'ยังไม่ได้กำหนด';
    return `${current.role_label}: ${names}`;
  }

  stationPersonNames(entry: DisbursementApprovalTrackEntry): string {
    return entry.person_names.length > 0 ? entry.person_names.join(', ') : 'ยังไม่ได้กำหนด';
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.datasource().total_page || page === this.currentPage) {
      return;
    }

    this.loadCertifications(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadCertifications(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadCertifications(1);
  }

  // --- Primary action (certify / approve / approve-revision) ---

  canActPrimary(row: CertificationRow | null): boolean {
    return this.resolvePrimaryActionKind(row) != null;
  }

  primaryActionButtonLabel(row: CertificationRow | null): string {
    return this.primaryActionButtonLabelForKind(this.resolvePrimaryActionKind(row));
  }

  onOpenPrimaryConfirm(): void {
    const kind = this.resolvePrimaryActionKind(this.selectedRow());
    if (kind == null) {
      return;
    }

    this.primaryActionKind.set(kind);
    this.primaryConfirmDialog.open();
  }

  onConfirmPrimaryAction(): void {
    const row = this.selectedRow();
    const kind = this.primaryActionKind();
    if (row == null || kind == null) {
      return;
    }

    this.actionLoading.set(true);
    this.resolvePrimaryActionCall(kind, row.id)
      .pipe(finalize(() => this.actionLoading.set(false)))
      .subscribe({
        next: () => {
          this.primaryConfirmDialog.close();
          this.closeDetailAfterAction();
          this.loadCertifications(this.currentPage);
          this.toast.toastSuccess(this.primaryActionSuccessMessage(kind));
        },
        error: (err) => {
          console.error(err);
          this.toast.toastError(this.extractErrorMessage(err));
        },
      });
  }

  primaryActionDialogTitle(): string {
    switch (this.primaryActionKind()) {
      case 'finance_approve':
        return 'ยืนยันการอนุมัติ';
      case 'revision_approve':
        return 'ยืนยันการอนุมัติการแก้ไขตารางสอน';
      case 'teacher_certify':
      case 'head_certify':
      case 'vice_dean_certify':
      case null:
      default:
        return 'ยืนยันการรับรอง';
    }
  }

  primaryActionDialogMessage(): string {
    switch (this.primaryActionKind()) {
      case 'finance_approve':
        return 'ต้องการอนุมัติใบรับรองการเบิกจ่ายนี้หรือไม่ ?';
      case 'revision_approve':
        return 'ต้องการอนุมัติการแก้ไขตารางสอนของอาจารย์ท่านนี้หรือไม่ ? (จะเข้าสู่ขั้นตอนแก้ไขตารางสอนพิเศษ)';
      case 'teacher_certify':
      case 'head_certify':
      case 'vice_dean_certify':
      case null:
      default:
        return 'ต้องการรับรองใบรับรองการเบิกจ่ายนี้หรือไม่ ?';
    }
  }

  primaryActionDialogConfirmText(): string {
    return this.primaryActionButtonLabelForKind(this.primaryActionKind());
  }

  // --- Revision / send-back action ---

  canShowRevisionButton(row: CertificationRow | null): boolean {
    return this.isRevisionActorForRow(row);
  }

  canOpenRevision(row: CertificationRow | null): boolean {
    return this.resolveRevisionActionKind(row) != null;
  }

  // While the edit window is OPEN, show the deadline so the actor knows how
  // long they have (the disabled-state tooltip already covers the CLOSED
  // case). Only teacher/head are time-windowed (Amendment A4) — finance and
  // vice_dean_administration send-backs, and vice_dean_academic's reject,
  // have no deadline concept at all, so this returns '' for them.
  revisionWindowLabel(row: CertificationRow | null): string {
    if (row == null || !this.canOpenRevision(row)) {
      return '';
    }

    const roleId = this.authService.getActiveRoleId();
    let deadline: string | null | undefined;

    if (roleId === RoleID.TEACHER) {
      deadline = row.raw.teacher_revision_deadline;
    } else if (roleId === RoleID.HEAD_OF_DEPARTMENT) {
      deadline = row.raw.head_revision_deadline;
    } else {
      return '';
    }

    if (deadline == null) {
      return '';
    }

    return `สามารถส่งกลับแก้ไขได้ถึงวันที่ ${this.formatDate(deadline)}`;
  }

  revisionButtonLabel(): string {
    const roleId = this.authService.getActiveRoleId();
    return roleId === RoleID.VICE_DEAN_ACADEMIC ? 'ไม่อนุมัติ' : 'ส่งกลับแก้ไข';
  }

  revisionDisabledTooltip(row: CertificationRow | null): string {
    if (row == null) {
      return '';
    }

    const deadline = this.authService.getActiveRoleId() === RoleID.HEAD_OF_DEPARTMENT
      ? row.raw.head_revision_deadline
      : row.raw.teacher_revision_deadline;

    if (deadline != null) {
      return `พ้นกำหนดการแก้ไข (เกินกำหนดวันที่ ${this.formatDate(deadline)}) — รับรองได้เท่านั้น`;
    }

    return 'พ้นกำหนดการแก้ไข — รับรองได้เท่านั้น';
  }

  onOpenRevisionDialog(): void {
    const row = this.selectedRow();
    const kind = this.resolveRevisionActionKind(row);
    if (row == null || kind == null) {
      return;
    }

    this.revisionActionKind.set(kind);
    this.revisionDialog.open();
  }

  onRevisionCancelled(): void {
    // Dialog closes itself; nothing else to reset since the kind is
    // recomputed fresh every time onOpenRevisionDialog runs.
  }

  onRevisionSubmitted(result: DisbursementRevisionDialogResult): void {
    const row = this.selectedRow();
    const kind = this.revisionActionKind();
    if (row == null || kind == null) {
      return;
    }

    this.actionLoading.set(true);
    this.buildRevisionRequest(kind, row.id, result)
      .pipe(finalize(() => this.actionLoading.set(false)))
      .subscribe({
        next: () => {
          this.revisionDialog.close();
          this.closeDetailAfterAction();
          this.loadCertifications(this.currentPage);
          this.toast.toastSuccess(this.revisionSuccessMessage(kind));
        },
        error: (err) => {
          console.error(err);
          this.toast.toastError(this.extractErrorMessage(err));
        },
      });
  }

  revisionDialogTitle(): string {
    return this.revisionActionKind() === 'vice_dean_academic_reject'
      ? 'ไม่อนุมัติการแก้ไขตารางสอน'
      : 'ส่งกลับแก้ไข';
  }

  revisionDialogConfirmText(): string {
    return this.revisionActionKind() === 'vice_dean_academic_reject'
      ? 'ยืนยันไม่อนุมัติ'
      : 'ยืนยันส่งกลับแก้ไข';
  }

  // --- Display helpers ---

  statusBadgeClass(status: DisbursementStatusCode): string {
    return STATUS_BADGE_CLASS[status];
  }

  formatDate(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return [
      String(date.getDate()).padStart(2, '0'),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(toBuddhistYear(date.getFullYear())),
    ].join('/');
  }

  private initColumns(): void {
    this.columns = [
      {
        prop: 'teacherName',
        name: 'อาจารย์',
        sortable: false,
        width: 190,
        minWidth: 170,
      },
      {
        prop: 'departmentName',
        name: 'สาขาวิชา',
        sortable: false,
        width: 170,
        minWidth: 150,
      },
      {
        prop: 'academicTermLabel',
        name: 'ภาคการศึกษา',
        sortable: false,
        width: 120,
        minWidth: 110,
      },
      {
        // v1.2 decision 8 — path split: a dual-curriculum teacher shows up as
        // two rows, distinguished by this column.
        prop: 'pathLabel',
        name: 'ภาค',
        sortable: false,
        width: 110,
        minWidth: 100,
      },
      {
        prop: 'status',
        name: 'สถานะ',
        sortable: true,
        cellTemplate: this.statusTemplate,
        width: 180,
        minWidth: 170,
      },
      {
        // v1.2 §3/§4 — compact "รอดำเนินการ: ตำแหน่ง ชื่อ" from approval_track.
        prop: 'pendingAction',
        name: 'รอดำเนินการ',
        sortable: false,
        cellTemplate: this.pendingActionTemplate,
        width: 220,
        minWidth: 180,
      },
      {
        prop: 'updatedAtLabel',
        name: 'อัปเดตล่าสุด',
        sortable: true,
        width: 130,
        minWidth: 120,
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.actionTemplate,
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        cellClass: 'text-center disb-cert-action-cell',
        headerClass: 'text-center disb-cert-action-header',
      },
    ];
  }

  private listenFilters(): void {
    this.subscriptions.add(
      this.filterForm.valueChanges
        .pipe(
          debounceTime(250),
          distinctUntilChanged(),
        )
        .subscribe(() => this.loadCertifications(1)),
    );
    this.subscriptions.add(
      this.filterForm.controls.academicYear.valueChanges.subscribe(() => this.onAcademicYearChanged()),
    );
    this.subscriptions.add(
      this.filterForm.controls.academicTerm.valueChanges.subscribe(() => this.syncAcademicTermId()),
    );
  }

  private onAcademicYearChanged(): void {
    const { academicYear, academicTerm } = this.filterForm.getRawValue();
    this.academicTermNameOptions = buildAcademicTermNameOptions(this.academicTerms, academicYear);
    const nextTerm = academicTerm != null && this.academicTermNameOptions.includes(academicTerm)
      ? academicTerm
      : this.academicTermNameOptions[0] ?? null;
    this.filterForm.patchValue({ academicTerm: nextTerm }, { emitEvent: false });
    this.syncAcademicTermId();
  }

  private syncAcademicTermId(): void {
    const { academicTerm, academicYear } = this.filterForm.getRawValue();
    const resolved = findAcademicTerm(this.academicTerms, academicTerm, academicYear);
    this.filterForm.patchValue({ academicTermId: resolved?.id ?? null }, { emitEvent: false });
  }

  private loadFilterOptions(): void {
    this.studyPeriodService.getAcademicTermYears().pipe(take(1)).subscribe({
      next: (terms) => {
        this.academicTerms = terms;
        this.academicYearOptions = buildAcademicYearOptions(terms);
        const defaultTerm = defaultAcademicTerm(terms);
        const defaultYear = defaultTerm != null ? String(toBuddhistYear(defaultTerm.year)) : null;
        this.academicTermNameOptions = buildAcademicTermNameOptions(terms, defaultYear);
        this.filterForm.patchValue(
          {
            academicYear: defaultYear,
            academicTerm: defaultTerm != null ? String(defaultTerm.name).trim() : null,
            academicTermId: defaultTerm?.id ?? null,
          },
          { emitEvent: false },
        );
        this.loadCertifications(1);
      },
      error: (err) => {
        console.error(err);
        this.loadCertifications(1);
      },
    });
  }

  // S4 deep-link landing (landing.component.ts#navigateToTarget) forwards a
  // `certification_id` query param when it routes here so a specific row can
  // open automatically instead of leaving the user to hunt for it in the
  // list (which may be on a different term/tab/page than the defaults this
  // component loads). Fetches the certification directly by id — the show
  // endpoint is itself role-scoped (api-contract.md §3: 403 outside the
  // actor's policy scope) — so a 403/404 here means "not in this actor's
  // scope," which is a silent no-op fallback to the plain list, never a
  // toast/error (the row simply not opening IS the correct behavior for an
  // out-of-scope id, not a failure to report).
  private maybeAutoOpenFocusedCertification(): void {
    const raw = this.route.snapshot.queryParamMap.get('certification_id');
    if (raw == null || raw.trim() === '') {
      return;
    }

    const certificationId = Number(raw);
    if (!Number.isFinite(certificationId) || certificationId <= 0) {
      return;
    }

    this.disbursementService.getCertification(certificationId).pipe(take(1)).subscribe({
      next: (cert) => {
        this.onView(this.mapCertificationToRow(cert));
      },
      error: () => {
        // Out of scope, or the certification no longer exists — fall back to
        // the plain list, no error surfaced (see method comment above).
      },
    });
  }

  private loadCertifications(page = this.currentPage): void {
    this.currentPage = page;
    this.tableLoading.set(true);
    this.disbursementService.getCertifications({
      ...this.buildFilterParams(),
      page,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort as DisbursementCertificationListParams['sort'],
      direction: this.currentSortParams.direction,
    }).pipe(
      finalize(() => this.tableLoading.set(false)),
    ).subscribe({
      next: (response) => {
        this.datasource.set(this.createDataSource(
          response.data.map((cert) => this.mapCertificationToRow(cert)),
          response.page,
          response.limit,
          response.total,
          response.total_pages,
        ));
      },
      error: (err) => {
        console.error(err);
        this.datasource.set(this.createDataSource([], page, this.currentLimit));
      },
    });
  }

  private buildFilterParams(): DisbursementCertificationListParams {
    const { academicTermId, keywords } = this.filterForm.getRawValue();
    const params: DisbursementCertificationListParams = {};

    if (academicTermId != null) {
      params.academic_term_id = academicTermId;
    }

    const normalizedKeywords = String(keywords ?? '').trim();
    if (normalizedKeywords !== '') {
      params.keywords = normalizedKeywords;
    }

    const status = this.statusForActiveTab();
    if (status != null) {
      params.status = status;
    }

    return params;
  }

  private statusForActiveTab(): DisbursementStatusCode | null {
    if (this.activeTab() !== 'pending') {
      return null;
    }

    const roleId = this.authService.getActiveRoleId();

    if (roleId === RoleID.TEACHER) {
      return 'pending_teacher';
    }

    if (roleId === RoleID.HEAD_OF_DEPARTMENT) {
      return 'pending_head_of_dept';
    }

    if (roleId === RoleID.FINANCE_OFFICER) {
      return 'pending_finance';
    }

    if (roleId === RoleID.VICE_DEAN_ADMINISTRATION) {
      return 'pending_vice_dean_admin';
    }

    if (roleId === RoleID.VICE_DEAN_ACADEMIC) {
      return 'revision_schedule_requested';
    }

    return null;
  }

  private resolvePrimaryActionKind(row: CertificationRow | null): PrimaryActionKind | null {
    if (row == null) {
      return null;
    }

    const roleId = this.authService.getActiveRoleId();

    if (roleId === RoleID.TEACHER && row.statusCode === 'pending_teacher') {
      return 'teacher_certify';
    }

    if (roleId === RoleID.HEAD_OF_DEPARTMENT && row.statusCode === 'pending_head_of_dept') {
      return 'head_certify';
    }

    if (roleId === RoleID.FINANCE_OFFICER && row.statusCode === 'pending_finance') {
      return 'finance_approve';
    }

    if (roleId === RoleID.VICE_DEAN_ADMINISTRATION && row.statusCode === 'pending_vice_dean_admin') {
      return 'vice_dean_certify';
    }

    if (roleId === RoleID.VICE_DEAN_ACADEMIC && row.statusCode === 'revision_schedule_requested') {
      return 'revision_approve';
    }

    return null;
  }

  private resolvePrimaryActionCall(kind: PrimaryActionKind, id: number): Observable<DisbursementCertificationModel> {
    switch (kind) {
      case 'teacher_certify':
        return this.disbursementService.teacherCertify(id);
      case 'head_certify':
        return this.disbursementService.headCertify(id);
      case 'finance_approve':
        return this.disbursementService.financeApprove(id);
      case 'vice_dean_certify':
        return this.disbursementService.viceDeanCertify(id);
      case 'revision_approve':
        return this.disbursementService.revisionApprove(id);
    }
  }

  private primaryActionSuccessMessage(kind: PrimaryActionKind): string {
    switch (kind) {
      case 'finance_approve':
        return 'อนุมัติใบรับรองการเบิกจ่ายเรียบร้อยแล้ว';
      case 'revision_approve':
        return 'อนุมัติการแก้ไขตารางสอนเรียบร้อยแล้ว';
      case 'teacher_certify':
      case 'head_certify':
      case 'vice_dean_certify':
      default:
        return 'รับรองใบรับรองการเบิกจ่ายเรียบร้อยแล้ว';
    }
  }

  private primaryActionButtonLabelForKind(kind: PrimaryActionKind | null): string {
    switch (kind) {
      case 'finance_approve':
        return 'อนุมัติ';
      case 'revision_approve':
        return 'อนุมัติการแก้ไข';
      case 'teacher_certify':
      case 'head_certify':
      case 'vice_dean_certify':
      case null:
      default:
        return 'รับรอง';
    }
  }

  // Whether the active role is *ever* the actor who sends this row back,
  // regardless of the backend's time-window boolean — drives whether the
  // button renders at all (vs. renders-disabled, see canOpenRevision/
  // revisionDisabledTooltip which layer the A4 gate on top of this).
  private isRevisionActorForRow(row: CertificationRow | null): boolean {
    if (row == null) {
      return false;
    }

    const roleId = this.authService.getActiveRoleId();

    if (roleId === RoleID.TEACHER) {
      return row.statusCode === 'pending_teacher';
    }

    if (roleId === RoleID.HEAD_OF_DEPARTMENT) {
      return row.statusCode === 'pending_head_of_dept';
    }

    if (roleId === RoleID.FINANCE_OFFICER) {
      return row.statusCode === 'pending_finance';
    }

    if (roleId === RoleID.VICE_DEAN_ADMINISTRATION) {
      return row.statusCode === 'pending_vice_dean_admin';
    }

    if (roleId === RoleID.VICE_DEAN_ACADEMIC) {
      return row.statusCode === 'revision_schedule_requested';
    }

    return false;
  }

  // Amendment A4: never compute the 7-day window client-side — gate strictly
  // on the backend's teacher_revision_allowed/head_revision_allowed booleans.
  // finance_officer/vice_dean_administration send-backs and
  // vice_dean_academic's reject have no such window (only the status gate).
  private resolveRevisionActionKind(row: CertificationRow | null): RevisionActionKind | null {
    if (!this.isRevisionActorForRow(row) || row == null) {
      return null;
    }

    const roleId = this.authService.getActiveRoleId();

    if (roleId === RoleID.TEACHER) {
      return row.raw.teacher_revision_allowed ? 'teacher_revision' : null;
    }

    if (roleId === RoleID.HEAD_OF_DEPARTMENT) {
      return row.raw.head_revision_allowed ? 'head_revision' : null;
    }

    if (roleId === RoleID.FINANCE_OFFICER) {
      return 'finance_revision';
    }

    if (roleId === RoleID.VICE_DEAN_ADMINISTRATION) {
      return 'vice_dean_admin_revision';
    }

    if (roleId === RoleID.VICE_DEAN_ACADEMIC) {
      return 'vice_dean_academic_reject';
    }

    return null;
  }

  private buildRevisionRequest(
    kind: RevisionActionKind,
    id: number,
    result: DisbursementRevisionDialogResult,
  ): Observable<DisbursementCertificationModel> {
    switch (kind) {
      case 'teacher_revision':
      case 'head_revision':
      case 'finance_revision':
      case 'vice_dean_admin_revision':
        return this.disbursementService.requestRevision(id, { comment: result.reason });
      case 'vice_dean_academic_reject':
        return this.disbursementService.revisionReject(id, result.reason);
    }
  }

  // v1.1: every send-back routes to รองวิชาการ regardless of origin station
  // (Amendment/decision #4), so the success copy no longer names a specific
  // next actor. Reject resumes the cert at the station after its recorded
  // origin (NEXT_STATION[origin]) — again not a fixed "back to head" hop.
  private revisionSuccessMessage(kind: RevisionActionKind): string {
    return kind === 'vice_dean_academic_reject'
      ? 'ดำเนินการไม่อนุมัติเรียบร้อยแล้ว'
      : 'ส่งคำขอแก้ไขตารางสอนไปยังรองวิชาการเรียบร้อยแล้ว';
  }

  private mapCertificationToRow(cert: DisbursementCertificationModel): CertificationRow {
    return {
      id: cert.id,
      teacherName: cert.user.full_name,
      teacherEmail: cert.user.email ?? '-',
      departmentName: cert.department.name_th ?? cert.department.name ?? cert.department.name_en ?? '-',
      academicTermLabel: `${cert.academic_term.name}/${toBuddhistYear(cert.academic_term.year)}`,
      pathLabel: cert.path_label,
      statusCode: cert.status_code,
      // Derived from the locked §6 enum map rather than trusting the
      // server's `status_label` string verbatim, so the Thai copy shown
      // here is guaranteed stable regardless of backend wording changes.
      statusLabel: DISBURSEMENT_STATUS_LABELS[cert.status_code],
      updatedAtLabel: this.formatDate(cert.updated_at),
      raw: cert,
    };
  }

  private createDataSource(
    rows: CertificationRow[],
    page = 1,
    limit = 10,
    total = rows.length,
    totalPage = Math.max(Math.ceil(total / limit), 1),
  ): CertificationDataSource {
    const normalizedPage = Math.min(Math.max(page, 1), totalPage);

    return {
      data: rows,
      limit,
      offset: (normalizedPage - 1) * limit,
      page: normalizedPage,
      total,
      total_page: totalPage,
    };
  }

  private extractErrorMessage(err: unknown): string {
    if (err !== null && typeof err === 'object' && 'error' in err) {
      const error = (err as { error?: unknown }).error;
      if (error !== null && typeof error === 'object' && 'errors' in error) {
        const errors = (error as { errors?: unknown }).errors;
        if (Array.isArray(errors) && errors.length > 0) {
          return String(errors[0]);
        }
        if (typeof errors === 'string') {
          return errors;
        }
      }
    }

    return 'ไม่สามารถดำเนินการได้';
  }
}
